import { OrderPickUpMode, OrderStatus } from "constants/orders";
import PeriodConfig from "models/PeriodConfig";
import { priceFormat } from "utils/mathUtils";

/**
 * This class represents an Order fetched from the API including all the needed business functions/methods related.
 */
class Order {
  /** ---------------- Mappers functions ---------------- */
  static fromAPI = (data) => {
    const status = Order.mapStatusFromAPI(data.status);
    return {
      cancelledQuantity: data.canceled_quantity,
      cancelledTotals: data.canceled_totals?.map((t) => Order.mapTotalFromAPI(t)) || [],
      // eslint-disable-next-line max-len,no-underscore-dangle
      canCreateInvoice: [OrderStatus.FULFILLED].includes(status) && !data.invoice?.id && !!data._actions?.create_invoice,
      cartUUID: data.cart_uuid,
      createdAt: data.creation_date,
      estimatedReadyAt: data.estimated_ready_date,
      hasBeenPaid: data.has_transaction,
      id: data.id,
      initialQuantity: data.initial_quantity,
      initialTotals: data.initial_totals?.map((t) => Order.mapTotalFromAPI(t)) || [],
      invoiceData: {
        customerData: data.invoice?.customerData,
        id: data.invoice?.id,
        reference: data.invoice?.reference,
      },
      preparationTime: data.preparation_time ? Math.round(data.preparation_time / 60) : null,
      pickUpInformation: {
        locationName: data.pick_up_information?.location_id,
        date: data.pick_up_information?.date,
      },
      pickUpLocations: data.pick_up_locations?.map((pul) => ({
        id: pul.id,
        name: pul.name,
        pickUpPeriodConfigs: pul.pick_up_period_configs?.map((pc) => PeriodConfig.fromAPI(pc)),
      })),
      pickUpMode: Order.mapPickUpModeFromAPI(data.pick_up_mode),
      pickUpReference: data.pick_up_reference,
      quantity: data.quantity,
      qrCode: data.qr_code,
      rows: data.rows?.map((r) => Order.mapRowFromAPI(r)),
      selectedPickUpLocation: {
        id: data.selected_pick_up_location?.id,
        name: data.selected_pick_up_location?.name,
      },
      selectedPickUpTimeSlot: {
        end: data.selected_pick_up_time_end_date,
        start: data.selected_pick_up_time_start_date,
      },
      shopId: data.shop_id,
      status,
      totals: data.totals?.map((t) => Order.mapTotalFromAPI(t)) || [],
      walletId: data.wallet_id,
      // eslint-disable-next-line no-underscore-dangle
      _actions: data._actions,
    };
  };

  /**
   * Map the pick-up mode returned by the API.
   */
  static mapPickUpModeFromAPI = (pickUpMode) => {
    const pickUpModeMapping = {
      d: OrderPickUpMode.DELIVERY_ONLY,
      dw: OrderPickUpMode.DELIVERY_OR_PICK_UP,
      n: OrderPickUpMode.NONE,
      w: OrderPickUpMode.PICK_UP_ONLY,
    };
    return Object.hasOwn(pickUpModeMapping, pickUpMode) ? pickUpModeMapping[pickUpMode] : pickUpMode;
  };

  /**
   * Map the row of the order returned by the API.
   */
  static mapRowFromAPI = (row) => ({
    cancelledQuantity: row.canceled_quantity,
    cancelledTotal: Order.mapTotalFromAPI(row.canceled_total),
    id: row.id,
    initialQuantity: row.initial_quantity,
    initialTotal: Order.mapTotalFromAPI(row.initial_total),
    menuRows: row.menu_rows?.map((mr) => ({
      id: mr.id,
      menuStepName: mr.menu_step_name,
      productId: mr.product_id,
      productName: mr.product_name,
      quantity: mr.quantity,
    })),
    productId: row.product_id,
    productName: row.product_name,
    quantity: row.quantity,
    total: Order.mapTotalFromAPI(row.total),
  });

  /**
   * Map the status returned by the API.
   */
  static mapStatusFromAPI = (status) => {
    const statusMapping = {
      C: OrderStatus.CANCELLED,
      F: OrderStatus.FULFILLED,
      I: OrderStatus.INITIATED,
      N: OrderStatus.NOT_FULFILLED,
      P: OrderStatus.PREPARING,
      R: OrderStatus.READY,
      TP: OrderStatus.TO_PREPARE,
    };
    return Object.hasOwn(statusMapping, status) ? statusMapping[status] : status;
  };

  /**
   * Map the totals return by the API.
   */
  static mapTotalFromAPI = (total) => ({
    amount: total.amount,
    currencyGroupId: total.currency_group_id,
    decimalPlaces: total.decimal_places,
    symbol: total.symbol,
  });

  /** ---------------- Business functions ---------------- */
  /**
   * Detect if the order is active according to its status.
   * @param status - The order status
   */
  static isOrderActive = (status) => (
    [
      OrderStatus.TO_PREPARE,
      OrderStatus.PREPARING,
      OrderStatus.READY,
      OrderStatus.INITIATED,
    ].includes(status)
  );

  /**
   * Detect if the order is archived according to its status.
   * @param status - The order status
   */
  static isOrderArchived = (status) => (
    [
      OrderStatus.FULFILLED,
      OrderStatus.NOT_FULFILLED,
      OrderStatus.CANCELLED,
    ].includes(status)
  );

  /**
   * This function format the total in a price format according to the given locale.
   * @param {object} total The total (in the specific format given by the model).
   * @param {string} locale The locale code to use for the price format.
   * @return {string} The formatted total.
   */
  static formatTotalForDisplay = (total, locale) => (
    priceFormat(total.amount, total.decimal_places, total.symbol, locale)
  );
}

export default Order;
